"use client";

import { TypePackageDetailWithCarousal } from "@contentful/types";
import { Container } from "@yardzen/ui/components/container";
import { cn } from "@yardzen/ui/lib/utils";
import { cva } from "class-variance-authority";
import { usePathname } from "next/navigation";
import { useState } from "react";
import { ImageCollection } from "./ImageCollection";
import { PackageDetailFloatingCard } from "./PackageDetailFloatingCard";
import { PackageDetailItem } from "./PackageDetailItem";

export function PackageDetailWithCarousel({
  packageDetailWithCarousel,
  lotSize,
  splitTreatment,
}: {
  packageDetailWithCarousel: TypePackageDetailWithCarousal<"WITHOUT_UNRESOLVABLE_LINKS">;
  lotSize?: string;
  splitTreatment?: boolean;
}) {
  const currentPath = usePathname();
  const isPackagesPage = currentPath.includes("/packages");

  const {
    package: packageDetailItem,
    carousal: imageCollection,
    minimalDetail,
    showPriceWhileMinimal,
  } = packageDetailWithCarousel?.fields || {};
  const [addOnTotal, setAddOnTotal] = useState(0);
  const modificationOptions = packageDetailItem?.fields.modificationOptions;
  const initialModificationOption = lotSize
    ? modificationOptions?.find(
        (option) => option?.fields.internalValue === lotSize,
      )
    : modificationOptions?.[0];

  const [currentModificationOption, setCurrentModificationOption] = useState(
    initialModificationOption,
  );

  if (!packageDetailItem && !imageCollection) {
    return null;
  }

  const variantResolved =
    packageDetailWithCarousel?.fields.variant ?? "preview";

  // Unique per banner so multiple detail items on one page don't collide on
  // #pdp-add-to-cart — the floating card observes this exact id.
  const addToCartId = `pdp-add-to-cart-${packageDetailWithCarousel.sys.id}`;

  const variants = cva("", {
    variants: {
      background: {
        detail: "",
        preview: "bg-brand-greens-dark bg-opacity-40",
      },
      gridContainer: {
        detail: "items-start lg:grid-cols-6 lg:gap-12",
        preview: "lg:grid-cols-2",
      },
      carouselColumn: {
        detail:
          "col-span-1 min-w-0 lg:col-span-3 order-first lg:order-last w-full",
        preview: "order-first",
      },
      textColumn: {
        detail: "col-span-1 min-w-0 lg:col-span-3",
        preview: "min-w-0",
      },
    },
  });

  return (
    <>
      <Container className="mt-6 max-w-[1350px]">
        <div
          className={cn(
            "w-full rounded-lg",
            variants({ background: variantResolved }),
          )}
        >
          <div
            className={cn(
              "grid grid-cols-1 items-center justify-center gap-4",
              variants({ gridContainer: variantResolved }),
            )}
          >
            {imageCollection && (
              <div
                className={variants({
                  carouselColumn: variantResolved,
                })}
              >
                <ImageCollection
                  imageCollection={imageCollection}
                  variant={variantResolved}
                />
              </div>
            )}
            {packageDetailItem && (
              <div className={variants({ textColumn: variantResolved })}>
                <PackageDetailItem
                  packageDetailItem={packageDetailItem}
                  variant={variantResolved}
                  lotSize={lotSize}
                  splitTreatment={splitTreatment}
                  packageImageUrl={
                    imageCollection?.fields.images[0]?.fields.image?.fields.file
                      ?.url
                  }
                  initialModificationOption={initialModificationOption}
                  currentModificationOption={currentModificationOption}
                  setCurrentModificationOption={setCurrentModificationOption}
                  onAddonPriceChange={setAddOnTotal}
                  isMinimalDetail={minimalDetail}
                  showPriceWhileMinimal={showPriceWhileMinimal}
                  addToCartId={addToCartId}
                />
              </div>
            )}
          </div>
        </div>
      </Container>
      <div>
        {!isPackagesPage && !minimalDetail && (
          <PackageDetailFloatingCard
            packageDetailBanner={packageDetailWithCarousel}
            splitTreatment={splitTreatment}
            addOnTotal={addOnTotal}
            addToCartId={addToCartId}
          />
        )}
      </div>
    </>
  );
}
